import { LINKS_SKELETON_ITEMS_COUNT } from "@/config/constants";

/**
 * Loading component for links
 * Displays link skeleton items when links are being loaded
 *
 * How it works:
 * 1. Renders multiple skeleton items that mimic the appearance of link items
 * 2. Uses animation to indicate loading state
 * 3. Provides visual feedback that content is loading
 *
 * Key features:
 * - Animated skeleton UI for better user experience
 * - Multiple skeleton items to represent a list of links
 * - Consistent styling with actual link items
 * - Clear visual indication of loading state
 *
 * Context:
 * This component is displayed by LinkList when links are being loaded.
 * It follows the same skeleton pattern as MediaAttachmentLoading and
 * FileAttachmentLoading for consistency.
 */
export default function LinkLoading() {
  // Create an array of 5 skeleton items
  const skeletonItems = Array.from(
    { length: LINKS_SKELETON_ITEMS_COUNT },
    (_, index) => (
      <div key={index} className="flex animate-pulse items-center gap-2 py-1">
        {/* Link preview image skeleton */}
        <div className="relative flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-muted">
          {/* Icon placeholder */}
          <div className="size-5 rounded-md bg-muted-foreground/5"></div>
        </div>

        {/* Link information skeleton */}
        <div className="text-md flex w-full min-w-0 flex-1 flex-col overflow-hidden">
          {/* Link title skeleton */}
          <div className="mb-1 h-5 w-4/5 rounded-md bg-muted"></div>

          {/* Link description skeleton */}
          <div className="mb-1 h-4 w-full rounded-md bg-muted"></div>
          <div className="mb-1 h-4 w-11/12 rounded-md bg-muted"></div>

          {/* Link domain skeleton */}
          <div className="mb-1 h-3 w-1/3 rounded-md bg-muted"></div>

          {/* Timestamp skeleton */}
          <div className="h-3 w-1/5 rounded-md bg-muted"></div>
        </div>

        {/* External link button skeleton */}
        <div className="ml-2 size-8 shrink-0 rounded-md bg-muted/50">
          <div className="flex size-full items-center justify-center">
            <div className="size-4 rounded-sm bg-muted-foreground/5"></div>
          </div>
        </div>
      </div>
    ),
  );

  return <div className="flex w-full flex-col gap-0 pt-1">{skeletonItems}</div>;
}
